import { Button, Dialog, DialogActions, DialogContent, LinearProgress } from '@mui/material';
import { FailureRemediationPlanResponse } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { TitleText } from '@/components/TitleText';
import { RemediableFailureKind } from '@/hooks/api/failures/remediableFailureKind';
import { useGetFailureRemediationPlan } from '@/hooks/api/failures/useGetFailureRemediationPlan';
import { useRemediateFailures } from '@/hooks/api/failures/useRemediateFailures';
import { useToast } from '@/hooks/useToast';
import { getApiError } from '@/types/apiError';
import { pluralize } from '@/utils/format';
import { describeRemediationOutcome, REMEDIATION_COPY, RemediationCopy } from './remediationCopy';
import { RemediationPlanBreakdown } from './RemediationPlanBreakdown';

const buttonSx = { textTransform: 'none', borderRadius: '12px', padding: '8px 16px' };

const describePlan = (
  copy: RemediationCopy,
  queuedCount: number,
  plan: FailureRemediationPlanResponse | undefined,
  isError: boolean
) => {
  if (isError) return `Could not load the ${copy.itemPlural} to ${copy.verb}. Close this and try again.`;
  if (!plan) return `Checking which failures a ${copy.verb} can fix...`;
  if (queuedCount === 0 && plan.alreadyTried > 0) return `Nothing new to ${copy.verb}.`;
  if (queuedCount === 0) return `Nothing here would recover with a ${copy.verb}.`;
  return `${pluralize(queuedCount, copy.itemSingular, copy.itemPlural)} will be queued to ${copy.verb}.`;
};

export const BatchRemediationDialog = ({ kind, onClose }: { kind: RemediableFailureKind; onClose: () => void }) => {
  const copy = REMEDIATION_COPY[kind];
  const { showToast } = useToast();
  const { data: plan, isLoading, isError } = useGetFailureRemediationPlan(kind);
  const { mutate, isPending } = useRemediateFailures();
  const queuedIds = (plan?.candidates ?? [])
    .filter(candidate => candidate.verdict !== 'file-problem')
    .map(candidate => candidate.id);

  const confirm = () =>
    mutate(
      { kind, ids: queuedIds },
      {
        onSuccess: result => {
          showToast(describeRemediationOutcome(result, copy));
          onClose();
        },
        onError: error =>
          showToast({ type: 'error', message: getApiError(error)?.message ?? `Could not ${copy.verb}.` }),
      }
    );

  return (
    <Dialog open fullWidth onClose={isPending ? undefined : onClose} data-testid='batch-remediation-dialog'>
      <TitleText sx={{ padding: '24px 24px 0 24px' }}>{`${copy.verbImperative} failed ${copy.itemPlural}`}</TitleText>
      <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
        <BodyText color='text.secondary' display='block'>
          {describePlan(copy, queuedIds.length, plan, isError)}
        </BodyText>
        {isLoading && <LinearProgress sx={{ mt: 2 }} data-testid='remediation-classifying' />}
        {plan && <RemediationPlanBreakdown plan={plan} copy={copy} />}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Button onClick={onClose} disabled={isPending} sx={buttonSx}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={confirm}
          disabled={isLoading || isPending || queuedIds.length === 0}
          sx={buttonSx}
        >
          {`${copy.verbImperative} ${pluralize(queuedIds.length, copy.itemSingular, copy.itemPlural)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
